'use client';

import { forwardRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableState } from '../../hooks/use-controllable-state';
import { Slot } from '../slot';
import { ToggleButtonGroupContext } from './context/toggle-button-group.context';
import { toggleButtonGroupRecipe } from './styles/toggle-button-group.styles';
import type { ToggleButtonGroupPropsInterface } from './types/toggle-button-group.types';

/**
 * Be sure to add an appropriate
 * [aria-label](https://www.w3.org/TR/wai-aria-1.2/#aria-label) for assistive technologies.
 *
 * @related ['ToggleButton', 'LoadingButton', 'TextButton', 'Button']
 * @since 8.0.0
 */

export const ToggleButtonGroup = forwardRef<HTMLDivElement, ToggleButtonGroupPropsInterface>(
  (props: ToggleButtonGroupPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      conjoined = true,
      children,
      className,
      css: cssProp,
      value,
      defaultValue,
      onValueChange,
      size = 'sm',
      disabled,
      ...rest
    } = props;

    const [valueState, setValueState] = useControllableState({
      prop: value,
      defaultProp: defaultValue,
      onChange: onValueChange,
    });

    const styles = css(
      toggleButtonGroupRecipe.raw({
        conjoined,
      }),
      cssProp,
    );
    const Component = asChild ? Slot : 'div';

    return (
      <ToggleButtonGroupContext.Provider
        value={{ size, value: valueState, setValue: setValueState, disabled }}
      >
        <Component
          ref={forwardedRef}
          data-c11n-component="ToggleButtonGroup"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          {...rest}
        >
          {children}
        </Component>
      </ToggleButtonGroupContext.Provider>
    );
  },
);

ToggleButtonGroup.displayName = 'ToggleButtonGroup';
